import { useEffect, useState } from 'react';
import { Alert, Button, Col, Divider, Drawer, Form, message, Row, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { SaveOutlined } from '@ant-design/icons';
import { useUserAuth } from '../../../context/UserContext';
import { MappingSummary, TableMappings, TemplateFormDetails } from './components';
import { useSelector } from 'react-redux';
import TemplateServices from '../../../services/templateServices';

const { Text } = Typography;

const NewTemplate = ({ open, onClose, getTemplates }: { open: boolean, onClose: () => void, getTemplates: () => void }) => {
    const { t } = useTranslation();
    const { user, selectedApp } = useUserAuth();
    const standardOrderFields = useSelector((state: any) => state.utils.standardOrderFields);
    const rpaTemplateTypes = useSelector((state: any) => state.utils.rpaTemplateTypes);
    const [form] = Form.useForm();
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingFieldsMapping, setLoadingFieldsMapping] = useState(false);
    const [formValues, setFormValues] = useState(null);
    const [dbFields, setDbFields] = useState([]);
    const [excelFields, setExcelFields] = useState([]);
    const [mappings, setMappings] = useState({});
    const [showMappings, setShowMappings] = useState(false);
    const {templateTypes} = useSelector((state: any) => state.utils);

    useEffect(() => {
        const dbResult = standardOrderFields.map(({ columN_NAME, columN_DESCRIPTION }: { columN_NAME: string, columN_DESCRIPTION: string }) => ({
            name: columN_NAME,
            description: columN_DESCRIPTION
        }));
        setDbFields(dbResult);
    }, [standardOrderFields]);

    useEffect(() => {
        const normalize = (s: string) => s.replace(/\s+/g, '').toLowerCase();

        // Track counters per base field name
        const matchCounters: { [key: string]: number } = {};
        const autoMapped: { [key: string]: { value: any, auto: boolean, isRequired: boolean } } = {};
        excelFields.forEach((ex: any) => {
            // Strip trailing number → ConditionType1 → ConditionType
            const exBaseName = ex.label.replace(/\d+$/, '');
            if (!matchCounters[exBaseName]) matchCounters[exBaseName] = 0;

            // Find all Excel fields matching the base name
            const possibleMatches = dbFields.filter(
                (db: any) => normalize(exBaseName) === normalize(db.name)
            );

            // Pick the match at current index, or null if not enough Excel fields
            const match = matchCounters[exBaseName] < possibleMatches.length
                ? possibleMatches[matchCounters[exBaseName]]
                : null;

            // Assign mapping - isRequired flag is always present, but defaults to false
            autoMapped[ex.label] = { value: match, auto: !!match, isRequired: false };

            // Increase counter for the next db field with same base name
            matchCounters[exBaseName]++;
        });

        setMappings(autoMapped);
    }, [dbFields, excelFields]);

    const onDrawerClose = () => {       
        form.resetFields();
        setFormValues(null);
        setMappings({});
        setShowMappings(false);
        setExcelFields([]);
        onClose();
    };

    const submitTemplateForm = (values: any) => {
        const fileList = values.file;
        if (!fileList || fileList.length === 0) {
            message.error(t('messages.uploadFileEmpty'));
            return;
        }
        const file = fileList[0].originFileObj;
        setFormValues(values);
        setShowMappings(true);
        generateFieldsFromExcel(file);
    };

    const generateFieldsFromExcel = (file: any) => {
        setLoadingFieldsMapping(true);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                const rawHeadersRowNum1 = Array.isArray(jsonData[0])
                    ? jsonData[0].map((col: any) => col?.toString?.() ?? '')
                    : []; // header are set in the first row of excel

                const rawHeaders = rawHeadersRowNum1;

                if (!rawHeaders || rawHeaders.length === 0) {
                    message.error(t('template.noHeaderFound'));
                    setShowMappings(false);
                    setLoadingFieldsMapping(false);
                    return;
                }

                // convert to objects with value=index, label=name
                const excelObjs = rawHeaders.map((label: any, index: number) => ({ value: index, label }));
                setExcelFields(excelObjs as any);
                setShowMappings(true);
                setLoadingFieldsMapping(false);

            } catch (err) {
                console.error(err);
                message.error('Failed to parse Excel file.');
                setLoadingFieldsMapping(false);
            }
        };
        reader.onerror = () => {
            message.error(t('template.failedToReadFile'));
            setShowMappings(false);
            setLoadingFieldsMapping(false);
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSelectChange = (exField: any, selected: any, fieldType: 'db' | 'isRequired') => {
        setMappings(prev => {
            const prevEntry: any = (prev as any)[exField] || {};

            if (fieldType === 'db') {
                // If user clears the DB select, also reset isRequired to false
                if (!selected) {
                    return {
                        ...prev,
                        [exField]: {
                            ...prevEntry,
                            value: null,
                            auto: false,
                            isRequired: false
                        }
                    };
                }

                return {
                    ...prev,
                    [exField]: {
                        ...prevEntry,
                        value: selected,
                        auto: false
                    }
                };
            }

            if (fieldType === 'isRequired') {
                return {
                    ...prev,
                    [exField]: {
                        ...prevEntry,
                        isRequired: selected
                    }
                };
            }

            return prev;
        });
    };

    const saveTemplate = async () => {
        const mappedArray = excelFields.map((ex: any) => ({
            excelField: ex.label,
            dbField: (mappings as any)[ex.label]?.value?.name || null,
            isRequired: (mappings as any)[ex.label]?.isRequired ?? false
        }));

        const payload: any = formValues;
        payload["applicationID"] = selectedApp?.id;
        payload["createdBy"] = user?.id;
        payload["templateFields"] = mappedArray as any;

        setLoadingSave(true);
        try {
            const respData = await TemplateServices.createTemplate(payload);
            if (respData.success) {
                message.success(t('messages.success'));
                getTemplates();
                onDrawerClose();
            }
            else {
                message.error(t('messages.errorAccured'));
            }

        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
        finally {
            setLoadingSave(false);
        }
    };

    return (
        <Drawer
            title={t('template.newTemplate')}
            width={1000}
            onClose={onDrawerClose}
            destroyOnClose
            keyboard={false}
            maskClosable={false}
            open={open}
            extra={
                <Space>
                    {showMappings && <Button loading={loadingSave} icon={<SaveOutlined />} type="primary" onClick={saveTemplate}>{t('template.saveTemplate')}</Button>}
                    <Button onClick={onDrawerClose}>{t('general.close')}</Button>
                </Space>
            }
        >
            {!showMappings && (
                <Row>
                    <Col span={24}>
                        <TemplateFormDetails templateTypes={templateTypes} form={form} submitTemplateForm={submitTemplateForm} loadingFieldsMapping={loadingFieldsMapping} formLocation="new" rpaTemplateTypes={rpaTemplateTypes} />
                    </Col>
                </Row>
            )}

            {showMappings && (
                <>
                    <Row gutter={30}>
                        <Col xs={24} sm={12} md={12} lg={12}>
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('template.templateName')}:</Text>
                                <Text strong>{(formValues as any)?.templateName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.salesOrganization')}:</Text>
                                <Text strong>{(formValues as any)?.salesOrganizationName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.soldTo')}:</Text>
                                <Text strong>{(formValues as any)?.soldToName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.shipTo')}:</Text>
                                <Text strong>{(formValues as any)?.shipToName || ''}</Text>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={12} style={{ borderLeft: "1px solid #e7e7e7" }}>
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.orderType')}:</Text>
                                <Text strong>{(formValues as any)?.orderTypeName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.purchaseOrderType')}:</Text>
                                <Text strong>{(formValues as any)?.purchasedOrderTypeName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.distributionChannel')}:</Text>
                                <Text strong>{(formValues as any)?.distributionChannelName || ''}</Text>
                            </Space>                            
                        </Col>
                    </Row>
                    <Divider />

                    <Row className="mb10">
                        <Col span={24}>
                            <Alert showIcon message={t('template.autoMappedMessage')} type="info" />
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={14}>
                            <TableMappings disableFields={loadingSave} dbFields={dbFields} mappings={mappings} excelFields={excelFields} handleSelectChange={handleSelectChange} />
                        </Col>
                        <Col span={10}>
                            <MappingSummary excelFields={excelFields} mappings={mappings} />
                        </Col>
                    </Row>
                </>
            )}
        </Drawer>
    );
};

export default NewTemplate;
